// echo — call and response. Hear a little call, then echo it back on any
// input path (pointer piano, typing, MIDI). The instrument leads, the timers
// follow: playing during the call's tail (or during review) simply starts
// your echo — no lost first notes, no button round-trips. The capture window
// is generous (longer while your echo is still shorter than the call) and
// the Done button always closes it on demand. Nothing auto-plays back at
// you; replay buttons are there when you want them. This IS ear training:
// the review compares your echo to the call (compareEcho — octave-tolerant,
// timing ignored) and says how close you landed, warmly. But retries are
// unlimited, nothing gates on success, and "skip" is always available.
// Ready to continue after every call has been visited. With `finisher: true`
// the last call stops short of home and is judged only on landing home.

import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  phraseLength,
  compareEcho,
  degreeOf,
  lastNote,
} from "../../../lib/homeKeyTheory";
import { PATCHES, playPhrase, resumeAudio } from "../../../lib/homeKeyAudio";
import Piano from "../Piano";
import useHomeKeyVoices from "../useHomeKeyVoices";
import useTypingKeys from "../useTypingKeys";
import useHomeKeyMidi from "../useHomeKeyMidi";

// Capture closes this long after your last note-off — patient while your
// echo is still shorter than the call, brisk once you've matched its length.
const WINDOW_SHORT_MS = 1800;
const WINDOW_LONG_MS = 3500;

export default function EchoMoment({ moment, onReadyChange }) {
  const calls = moment.calls || [];
  const [callIndex, setCallIndex] = useState(0);
  const [phase, setPhase] = useState("idle"); // idle | call | capturing | review | done
  const [echo, setEcho] = useState(null); // captured phrase for this call
  const [result, setResult] = useState(null); // compareEcho of the last take
  const [noteCount, setNoteCount] = useState(0);
  const [impression, setImpression] = useState(null); // optional reflect pick
  const phaseRef = useRef(phase);
  phaseRef.current = phase;
  const callIndexRef = useRef(callIndex);
  callIndexRef.current = callIndex;

  const recState = useRef({ t0: null, starts: new Map(), notes: [] });
  const windowTimer = useRef(null);
  const playRef = useRef(null);
  const timersRef = useRef([]);

  const stopPlayback = useCallback(() => {
    if (playRef.current && playRef.current.stop) playRef.current.stop();
    playRef.current = null;
    timersRef.current.forEach((t) => clearTimeout(t));
    timersRef.current = [];
    if (windowTimer.current) clearTimeout(windowTimer.current);
    windowTimer.current = null;
  }, []);
  useEffect(() => () => stopPlayback(), [stopPlayback]);

  const ready = phase === "done";
  useEffect(() => {
    if (onReadyChange) onReadyChange(ready);
  }, [ready, onReadyChange]);

  // ----- capture plumbing (fed by useHomeKeyVoices' recorder wiring) -----

  const setPhaseNow = (next) => {
    // phaseRef must move with the phase synchronously — recorder callbacks
    // fire between renders.
    phaseRef.current = next;
    setPhase(next);
  };

  const beginCapture = () => {
    stopPlayback(); // if the call is still ringing, you've taken over
    recState.current = { t0: null, starts: new Map(), notes: [] };
    setEcho(null);
    setResult(null);
    setNoteCount(0);
    setImpression(null);
    setPhaseNow("capturing");
  };

  const finishCapture = () => {
    if (phaseRef.current !== "capturing") return;
    voices.releaseAll(); // closes (and records) anything still held
    if (windowTimer.current) clearTimeout(windowTimer.current);
    windowTimer.current = null;
    const notes = [...recState.current.notes].sort((a, b) => a.at - b.at);
    const phrase = notes.length ? { notes } : null;
    setEcho(phrase);
    setResult(phrase ? compareEcho(calls[callIndexRef.current], phrase) : null);
    setPhaseNow("review");
  };

  const recorder = {
    onNoteStart: (midi, tMs) => {
      const ph = phaseRef.current;
      // The instrument leads: playing over the call's tail, or playing again
      // from review, simply starts (re)capturing — this very note included.
      if (ph === "call" || ph === "review") beginCapture();
      else if (ph !== "capturing") return;
      const r = recState.current;
      if (r.t0 == null) r.t0 = tMs;
      r.starts.set(midi, tMs);
      if (windowTimer.current) clearTimeout(windowTimer.current);
      windowTimer.current = null;
    },
    onNoteEnd: (midi, tMs) => {
      if (phaseRef.current !== "capturing") return;
      const r = recState.current;
      const start = r.starts.get(midi);
      if (start == null) return;
      r.starts.delete(midi);
      r.notes.push({
        midi,
        at: Math.round(start - r.t0),
        dur: Math.max(40, Math.round(tMs - start)),
      });
      setNoteCount(r.notes.length);
      if (r.starts.size === 0) {
        const callNotes = ((calls[callIndexRef.current] || {}).notes || []).length;
        const wait = r.notes.length >= callNotes ? WINDOW_SHORT_MS : WINDOW_LONG_MS;
        windowTimer.current = setTimeout(finishCapture, wait);
      }
    },
  };

  const voices = useHomeKeyVoices(PATCHES.softKeys, recorder);
  const { press, release, activeMidis } = voices;
  useTypingKeys(press, release, phase !== "done");
  useHomeKeyMidi(press, release, phase !== "done");

  // ----- flow -----

  const playCall = (index = callIndexRef.current) => {
    resumeAudio();
    stopPlayback();
    setEcho(null);
    setResult(null);
    setNoteCount(0);
    setImpression(null);
    setPhaseNow("call");
    const call = calls[index];
    const handle = playPhrase(call, PATCHES.softKeys);
    playRef.current = handle && handle.stop ? handle : null;
    // Open capture right when the call's last note ends — not after the
    // ring-out — so echoing over the tail feels natural (and any earlier
    // press opens it even sooner via the recorder).
    const openMs = (phraseLength(call) || 2000) + 150;
    timersRef.current.push(
      setTimeout(() => {
        if (phaseRef.current === "call") {
          recState.current = { t0: null, starts: new Map(), notes: [] };
          setPhaseNow("capturing");
        }
      }, openMs)
    );
  };

  const replayCall = () => {
    resumeAudio();
    stopPlayback();
    playRef.current = playPhrase(calls[callIndex], PATCHES.softKeys);
  };
  const replayEcho = () => {
    if (!echo) return;
    resumeAudio();
    stopPlayback();
    playRef.current = playPhrase(echo, PATCHES.softKeys);
  };

  const advance = () => {
    stopPlayback();
    voices.releaseAll();
    setEcho(null);
    setResult(null);
    setNoteCount(0);
    setImpression(null);
    if (callIndex >= calls.length - 1) setPhaseNow("done");
    else {
      const next = callIndex + 1;
      setCallIndex(next);
      // Roll straight into the next call — the click that got you here is
      // already the user gesture the browser wants.
      playCall(next);
    }
  };

  const isLast = callIndex >= calls.length - 1;
  const finisherCall = isLast && moment.finisher;

  // Honest, warm feedback: how close the echo landed. Finisher calls are
  // creative (add your own ending), so they're judged only on the landing.
  const feedback = () => {
    if (!echo) return "nothing caught that time — hear it again whenever you like, or just start playing.";
    if (finisherCall) {
      const landedHome = degreeOf((lastNote(echo) || {}).midi) === 1;
      return landedHome
        ? "…and you brought it home. Feel that little click of arrival? That's the whole lesson."
        : "you left it hanging — hear the itch? One more go: end on a home key (the marked Gs).";
    }
    const r = result || compareEcho(calls[callIndex], echo);
    if (r.perfect) return "note for note. That's your ear and your hands agreeing — again for fun, or onward.";
    const extras = r.extra > 0 ? " (a couple of extra notes snuck in.)" : "";
    if (r.matched >= Math.max(1, Math.ceil(r.total * 0.6))) {
      const where =
        r.firstMissDelta == null
          ? `note ${r.firstMissIndex + 1} went missing`
          : `note ${r.firstMissIndex + 1} drifted ${r.firstMissDelta > 0 ? "high" : "low"}`;
      return `close — ${r.matched} of ${r.total}, and ${where}. Play the call once more and listen for it.${extras}`;
    }
    return `you caught ${r.matched} of ${r.total}. Try humming the call before you play it — humming makes the ear commit.${extras}`;
  };

  return (
    <div className="hk-moment-body">
      <p>
        {moment.prompt ||
          "Hear a little call, then echo it back — with your mouse, your typing keyboard, or a MIDI one. We'll tell you how close you landed; replay and retry as much as you like."}
      </p>

      {phase !== "done" && (
        <p className="hk-echo-status">
          call {callIndex + 1} of {calls.length}
          {finisherCall &&
            " — this one stops short of home. Finish it for us: bring it home."}
        </p>
      )}

      {phase === "idle" && (
        <div className="hk-choice-row">
          <button type="button" className="hk-demo-btn" onClick={() => playCall()}>
            ▶ hear the call
          </button>
          <button type="button" className="hk-btn hk-btn--ghost" onClick={advance}>
            skip this one
          </button>
        </div>
      )}

      {phase === "call" && (
        <p className="hk-echo-status">listening… (jump in whenever you like)</p>
      )}

      {phase === "capturing" && (
        <>
          <p className="hk-echo-status" aria-live="polite">
            your turn — echo it back. take your time.
            {noteCount > 0 && ` caught ${noteCount} note${noteCount === 1 ? "" : "s"}…`}
          </p>
          <div className="hk-choice-row">
            <button type="button" className="hk-btn" onClick={finishCapture}>
              done
            </button>
            <button type="button" className="hk-btn hk-btn--ghost" onClick={() => playCall()}>
              hear it again first
            </button>
            <button type="button" className="hk-btn hk-btn--ghost" onClick={advance}>
              skip
            </button>
          </div>
        </>
      )}

      {phase === "review" && (
        <>
          <p className="hk-echo-status">{feedback()}</p>
          {echo && !finisherCall && (
            <p className="hk-echo-hint">want another go? just start playing.</p>
          )}
          {echo && moment.reflect && (
            <div className="hk-choice-row" role="group" aria-label="how did that ending feel?">
              {moment.reflect.map((word) => (
                <button
                  key={word}
                  type="button"
                  className={`hk-choice${impression === word ? " hk-choice--picked" : ""}`}
                  aria-pressed={impression === word}
                  onClick={() => setImpression(word)}
                >
                  {word}?
                </button>
              ))}
            </div>
          )}
          <div className="hk-choice-row">
            <button type="button" className="hk-demo-btn" onClick={replayCall}>
              ▶ the call
            </button>
            {echo && (
              <button type="button" className="hk-demo-btn" onClick={replayEcho}>
                ▶ your echo
              </button>
            )}
            <button type="button" className="hk-btn hk-btn--ghost" onClick={() => playCall()}>
              again
            </button>
            <button type="button" className="hk-btn hk-btn--primary" onClick={advance}>
              next
            </button>
          </div>
        </>
      )}

      {phase === "done" && (
        <p className="hk-echo-status">echoes done — that was the practice. Onward.</p>
      )}

      <Piano activeMidis={activeMidis} onPress={press} onRelease={release} disabled={phase === "done"} />
    </div>
  );
}
